import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import {
  Tabs as ShadcnTabs,
  TabsList as ShadcnTabsList,
  TabsTrigger as ShadcnTabsTrigger,
  TabsContent as ShadcnTabsContent,
} from "@/components/ui/tabs";
import { cn } from "@/lib/utils/common";

const tabsListVariants = cva("inline-flex items-center justify-center", {
  variants: {
    variant: {
      primary: ["h-10 rounded-md bg-muted p-1"],
      secondary: ["h-auto border-b border-border bg-transparent p-0", "text-muted-foreground"],
    },
  },
  defaultVariants: {
    variant: "primary",
  },
});

const tabsTriggerVariants = cva(
  [
    "inline-flex items-center justify-center whitespace-nowrap font-medium",
    "ring-offset-background transition-all",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:pointer-events-none disabled:opacity-50",
  ],
  {
    variants: {
      variant: {
        primary: [
          "rounded-sm px-3 py-1.5 text-sm",
          "data-[state=active]:bg-background data-[state=active]:text-foreground",
          "data-[state=active]:shadow-sm",
          "hover:bg-background/50 data-[state=active]:hover:bg-background",
        ],
        secondary: [
          "relative rounded-none px-4 py-3 text-sm border-b-2 border-transparent",
          "data-[state=active]:border-primary data-[state=active]:text-foreground",
          "hover:text-foreground hover:border-primary/50",
          "transition-colors duration-200",
        ],
      },
    },
    defaultVariants: {
      variant: "primary",
    },
  },
);

export interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnTabsList>,
    VariantProps<typeof tabsListVariants> {}

export interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnTabsTrigger>,
    VariantProps<typeof tabsTriggerVariants> {}

const Tabs = ShadcnTabs;

const TabsList = React.forwardRef<React.ElementRef<typeof ShadcnTabsList>, TabsListProps>(
  ({ className, variant, ...props }, ref) => (
    <ShadcnTabsList ref={ref} className={cn(tabsListVariants({ variant }), className)} {...props} />
  ),
);
TabsList.displayName = "TabsList";

const TabsTrigger = React.forwardRef<React.ElementRef<typeof ShadcnTabsTrigger>, TabsTriggerProps>(
  ({ className, variant, ...props }, ref) => (
    <ShadcnTabsTrigger ref={ref} className={cn(tabsTriggerVariants({ variant }), className)} {...props} />
  ),
);
TabsTrigger.displayName = "TabsTrigger";

const TabsContent = ShadcnTabsContent;

export { Tabs, TabsList, TabsTrigger, TabsContent };
